import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appendToAssistantDetail } from '../../store/app/assistantSlice';

function StructuredDataComponent() {
    const dispatch = useDispatch();
    const assistantDetails = useSelector((state) => state.assistants.activeDetail.details);

    const initialPrompt =
        assistantDetails?.analysisPlan?.structuredDataPlan?.messages?.[0]?.content || '';

    const initialProperties = Object.entries(
        assistantDetails?.analysisPlan?.structuredDataPlan?.schema?.properties || {}
    ).map(([name, schema]) => ({
        name,
        type: schema.type,
        isEnum: !!schema.enum,
        enums: schema.enum || [],
        required: (assistantDetails.analysisPlan.structuredDataPlan.schema.required || []).includes(name),
    }));

    const [prompt, setPrompt] = useState(initialPrompt);
    const [properties, setProperties] = useState(initialProperties);

    useEffect(() => {
        const schemaProperties = {};
        const requiredFields = [];

        properties.forEach((prop) => {
            const { name, type, isEnum, enums, required } = prop;
            if (!name) return;

            schemaProperties[name] = isEnum
                ? { type, enum: enums.filter((e) => e) }
                : { type };

            if (required) requiredFields.push(name);
        });

        const structuredDataPlan = {
            enabled: true,
            schema: {
                type: 'object',
                properties: schemaProperties,
                ...(requiredFields.length ? { required: requiredFields } : {}),
            },
            messages: [
                {
                    role: 'system',
                    content: prompt || '',
                },
            ],
        };

        const existingPlan = assistantDetails?.analysisPlan || {};
        dispatch(
            appendToAssistantDetail({
                analysisPlan: {
                    ...existingPlan,
                    structuredDataPlan,
                },
            })
        );
    }, [prompt, properties, dispatch, assistantDetails?.analysisPlan]);

    const handlePropertyChange = (index, key, value) => {
        setProperties((prev) => {
            const updated = [...prev];
            updated[index][key] = value;

            // If toggling isEnum off, clear enums
            if (key === 'isEnum' && !value) {
                updated[index].enums = [];
            }
            return updated;
        });
    };

    const handleEnumChange = (index, enumIndex, value) => {
        setProperties((prev) => {
            const updated = [...prev];
            updated[index].enums[enumIndex] = value;
            return updated;
        });
    };
    const addProperty = () => {
        setProperties((prev) => [
            ...prev,
            { name: '', type: 'string', isEnum: false, enums: [], required: false },
        ]);
    };

    const removeProperty = (index) => {
        setProperties((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="card">
            <div
                style={{
                    borderTopRightRadius: '8px',
                    borderTopLeftRadius: '8px',
                    backgroundColor: '#f1f3f5',
                    padding: '10px 20px',
                }}
            >
                <div>Structured Data</div>
                <div className="text-xs mb-10">
                    Extract structured data from call conversation. You can use Data Schema standalone or in combination with Structured Data Prompt. If both are provided, they are concatenated into appropriate instructions.
                </div>
            </div>

            <div style={{ padding: '10px 20px' }}>
                <div>Prompt</div>
                <div className="text-xs mb-10">
                    This is the prompt that's used to extract structured data from the call.
                </div>
                <textarea
                    style={{ height: '300px' }}
                    className="agent-form-textarea agent-form-textarea::placeholder"

                    value={prompt}
                    placeholder="You will be given a transcript of a call and the system prompt of the Al participant. Extract..."
                    onChange={(e) => setPrompt(e.target.value)}
                />
            </div>

            <hr />

            <div style={{ padding: '10px 20px' }}>
                <div className='mb-10'>Properties</div>
                {properties.map((prop, idx) => (
                    <div
                        key={idx}
                        className="border rounded p-3 mb-3"
                        style={{ backgroundColor: '#f9f9f9' }}
                    >
                        <div className="row mb-2">
                            <div className="col">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={prop.name}
                                    onChange={(e) => handlePropertyChange(idx, 'name', e.target.value)}
                                />
                            </div>
                            <div className="col">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    style={{
                                        backgroundPosition: 'right 1.25rem center',
                                    }}
                                    value={prop.type}
                                    onChange={(e) => handlePropertyChange(idx, 'type', e.target.value)}
                                >
                                    <option value="string">String</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Boolean</option>
                                    <option value="object">Object</option>
                                    <option value="array">Array</option>
                                </select>
                            </div>
                            <div className="col-auto d-flex align-items-end">
                                <button
                                    className="btn btn-danger btn-md"
                                    onClick={() => removeProperty(idx)}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <div className="row mt-20">
                            <div className="d-flex gap-20">
                                <div className="gap-10">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={prop.required}
                                        onChange={(e) => handlePropertyChange(idx, 'required', e.target.checked)}
                                    />
                                    <label className="form-check-label">Required</label>
                                </div>
                                <div className="gap-10">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={prop.isEnum}
                                        onChange={(e) => handlePropertyChange(idx, 'isEnum', e.target.checked)}
                                    />
                                    <label className="form-check-label">Is Enum</label>
                                </div>
                            </div>
                        </div>

                        {prop.enums.map((enumVal, enumIdx) => (
                            <div key={enumIdx} className="d-flex gap-2 mb-1 d-flex">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={`Enum ${enumIdx + 1}`}
                                    value={enumVal}
                                    onChange={(e) =>
                                        handleEnumChange(idx, enumIdx, e.target.value)
                                    }
                                />
                               
                            </div>
                        ))}


                    </div>
                ))}

                <button className="btn btn-primary" onClick={addProperty}>
                    + Add Property
                </button>
            </div>
        </div>
    );
}

export default StructuredDataComponent;
